/**
 * Referrals and Punya — `/v1/admin/referrals/*` and `/v1/admin/punya/*`.
 *
 * **Two scopes behind one screen.** The programme dashboard and the fraud rulings are `growth`'s;
 * the two Punya writes are `finance`'s, because Punya is a liability the business carries and a
 * manual credit is money leaving it. The server enforces the split — these hooks only make sure
 * the console does not offer a door it will refuse to open.
 *
 * **Nothing here values a point.** `outstanding_paise` and `redeem_paise_per_point` both come
 * from the liability report, because the rate is a growth-owned setting that moves: a console
 * that multiplied points by a constant would report a liability that disagreed with finance's
 * the first time somebody changed the dial.
 *
 * **`points` on a transaction row is SIGNED.** A reversal credits or debits depending on what it
 * compensates, so the sign lives on the row and must never be re-derived from `entry_type`.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type ReferralRow = components['schemas']['ReferralRow'];
export type ReferralDetail = components['schemas']['ReferralDetail'];
export type ReferralRewardRow = components['schemas']['ReferralRewardRow'];
export type PunyaStatement = components['schemas']['PunyaStatement'];
export type PunyaTransactionRow = components['schemas']['PunyaTransactionRow'];
export type PunyaLiabilityRow = components['schemas']['PunyaLiabilityRow'];
export type PunyaAdjustIn = components['schemas']['PunyaAdjustIn'];
export type PunyaAdjustmentRow = components['schemas']['PunyaAdjustmentRow'];
type PunyaAdjustmentList = components['schemas']['PunyaAdjustmentListResponse'];
type ReferralList = components['schemas']['ReferralListResponse'];

export interface ReferralQuery extends Record<string, unknown> {
  page: number;
  page_size: number;
  status?: string;
}

function search(params: ReferralQuery): string {
  const query = new URLSearchParams();
  query.set('page', String(params.page));
  query.set('page_size', String(params.page_size));
  // Only when it has a value: an empty `status=` is a filter on the empty string to a server,
  // not the absence of one, so a cleared dropdown would find nothing at all.
  if (params.status) query.set('status', params.status);
  return query.toString();
}

export function useReferrals(params: ReferralQuery) {
  return useQuery({
    queryKey: keys.loyalty.referrals(params),
    queryFn: () => authedClient.request<ReferralList>(`/v1/admin/referrals?${search(params)}`),
  });
}

export function useReferral(referralId: number | null) {
  return useQuery({
    queryKey: keys.loyalty.referral(referralId ?? 0),
    queryFn: () => authedClient.request<ReferralDetail>(`/v1/admin/referrals/${referralId}`),
    enabled: referralId !== null,
  });
}

export function useRejectReferral() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { referralId: number; reason: string }) =>
      authedClient.request<ReferralDetail>(`/v1/admin/referrals/${input.referralId}/reject`, {
        method: 'POST',
        body: { reason: input.reason },
      }),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.loyalty.all() }),
  });
}

export function useRequalifyReferral() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (referralId: number) =>
      authedClient.request<ReferralDetail>(`/v1/admin/referrals/${referralId}/requalify`, {
        method: 'POST',
      }),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.loyalty.all() }),
  });
}

export function usePunyaStatement(userId: number | null, page = 1) {
  return useQuery({
    queryKey: keys.loyalty.statement(userId ?? 0, page),
    queryFn: () =>
      authedClient.request<PunyaStatement>(
        `/v1/admin/punya/accounts/${userId}?page=${page}&page_size=50`,
      ),
    enabled: userId !== null,
  });
}

export function usePunyaLiability() {
  return useQuery({
    queryKey: keys.loyalty.liability(),
    queryFn: () => authedClient.request<PunyaLiabilityRow>('/v1/admin/punya/liability-report'),
  });
}

export function useAdjustPunya() {
  const client = useQueryClient();
  return useMutation({
    /* This ASKS; it moves nothing. The request parks as `pending` and only a different finance
       admin's own approve writes the movement (doc 38 §4.7). It used to carry a typed
       `second_approver_admin_id`, which made the four-eyes control a number one person could
       type — so there is no seconder field here at all. */
    mutationFn: (body: PunyaAdjustIn) =>
      authedClient.request<PunyaAdjustmentRow>('/v1/admin/punya/adjust', {
        method: 'POST',
        body,
      }),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.loyalty.all() }),
  });
}

export function usePunyaAdjustments(status: 'pending' | 'applied' | 'rejected') {
  return useQuery({
    queryKey: keys.loyalty.adjustments(status),
    queryFn: () =>
      authedClient.request<PunyaAdjustmentList>(`/v1/admin/punya/adjustments?status=${status}`),
  });
}

/** Countersign or refuse. The server refuses the requester (403 self_approval_forbidden). */
export function useDecidePunyaAdjustment() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      decision,
      reason,
    }: {
      id: number;
      decision: 'approve' | 'reject';
      reason?: string;
    }) =>
      authedClient.request<PunyaAdjustmentRow>(`/v1/admin/punya/adjustments/${id}/${decision}`, {
        method: 'POST',
        ...(decision === 'reject' ? { body: { reason } } : {}),
      }),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.loyalty.all() }),
  });
}
